import { ERROR_401, ERROR_403 } from 'common/config/api';

/**
 * Extracts the HTTP status code from an unknown error value.
 * Handles both OpenAPI-generated errors ({ status }) and axios response errors ({ response.status }).
 * String statuses (e.g. from legacy error shapes) are coerced to numbers.
 */
export const getAuthErrorStatus = (error: unknown): number | null => {
  if (typeof error !== 'object' || error === null) {
    return null;
  }

  if ('status' in error) {
    const s = (error as { status?: unknown }).status;
    if (typeof s === 'number') {
      return s;
    }
    if (typeof s === 'string') {
      const n = Number(s);
      return Number.isFinite(n) ? n : null;
    }
  }

  if ('response' in error) {
    const s = (error as { response?: { status?: unknown } }).response?.status;
    if (typeof s === 'number') {
      return s;
    }
  }

  return null;
};

/**
 * Returns true when the error represents a definitive auth rejection (401 or 403).
 * These are the only statuses that should trigger session clearing; 5xx and network
 * errors are transient and should leave stored tokens intact.
 */
export const isDefinitiveAuthRejection = (error: unknown): boolean => {
  const s = getAuthErrorStatus(error);
  return s === Number(ERROR_401) || s === Number(ERROR_403);
};

/**
 * Thrown when the refresh attempt fails transiently (network or 5xx error)
 * The stored tokens remain intact — callers should avoid marking the session
 * as verified and allow a future retry.
 */
export class TransientRefreshError extends Error {
  constructor() {
    super('transient_refresh');
    this.name = 'TransientRefreshError';
  }
}

